import type { GameDetail, GameEfficiency, ModelStats, EnhancedModelStats, ModelDetailStats, HeadToHeadRecord, HeadToHeadComparison, OpeningStats, EloHistoryPoint, CreateGameRequest, GameCreatedResponse, PlatformOverview, OpenRouterModel, PaginatedGamesResponse, QueueStatus } from "../types/api";

const BASE = "/api";
const DEFAULT_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  status: number;
  retryAfter?: number;
  body?: string;

  constructor(message: string, status: number, opts?: { retryAfter?: number; body?: string }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = opts?.retryAfter;
    this.body = opts?.body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (init?.signal?.aborted) abort();
  else init?.signal?.addEventListener("abort", abort, { once: true });
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: { "Content-Type": "application/json" },
      ...init,
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let detail: string | undefined;
      try {
        const parsed = JSON.parse(body).detail;
        if (typeof parsed === "string") detail = parsed;
      } catch {
        // Non-JSON error body.
      }
      if (res.status === 429) {
        const header = res.headers.get("Retry-After");
        const retryAfter = header && !Number.isNaN(Number(header)) ? Number(header) : undefined;
        throw new ApiError(
          detail ?? (retryAfter != null
            ? `Rate limited — retrying in ${retryAfter}s`
            : "Rate limited — please wait a moment and try again"),
          res.status,
          { retryAfter, body },
        );
      }
      throw new ApiError(
        detail ?? `Request failed (${res.status})`,
        res.status,
        { body },
      );
    }
    return res.json();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError("Request timed out — check your connection.", 0);
    }
    if (err instanceof TypeError) {
      throw new ApiError("Network error — could not reach the server.", 0);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    init?.signal?.removeEventListener("abort", abort);
  }
}

export async function listGames(params?: {
  status?: string;
  model?: string;
  outcome?: string;
  opening?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedGamesResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.model) qs.set("model", params.model);
  if (params?.outcome) qs.set("outcome", params.outcome);
  if (params?.opening) qs.set("opening", params.opening);
  if (params?.q) qs.set("q", params.q);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const query = qs.toString();
  return request<PaginatedGamesResponse>(`/games${query ? `?${query}` : ""}`);
}

export async function getGame(gameId: string): Promise<GameDetail> {
  return request<GameDetail>(`/games/${gameId}`);
}

export async function getGameEfficiency(gameId: string, signal?: AbortSignal): Promise<GameEfficiency> {
  return request<GameEfficiency>(`/games/${gameId}/efficiency`, { signal });
}

export async function createGame(req: CreateGameRequest, adminToken?: string | null): Promise<GameCreatedResponse> {
  return request<GameCreatedResponse>("/games", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(adminToken ? { "X-Admin-Token": adminToken } : {}) },
    body: JSON.stringify(req),
  });
}

export async function stopGame(gameId: string, playerSecret: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/games/${gameId}/stop`, {
    method: "POST",
    body: JSON.stringify({ player_secret: playerSecret }),
  });
}

export async function deleteGame(gameId: string, adminToken: string): Promise<void> {
  // DELETE returns 204 (no body), so we don't go through `request` (which parses JSON).
  const res = await fetch(`${BASE}/games/${gameId}`, {
    method: "DELETE",
    headers: { "X-Admin-Token": adminToken },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const msg =
      res.status === 403
        ? "Admin token rejected."
        : res.status === 404
          ? "Admin actions are not enabled on this server."
          : `Delete failed (${res.status})`;
    throw new ApiError(msg, res.status, { body });
  }
}

export async function listModels(): Promise<ModelStats[]> {
  return request<ModelStats[]>("/models");
}

export async function getLeaderboard(): Promise<EnhancedModelStats[]> {
  return request<EnhancedModelStats[]>("/models/leaderboard");
}

export async function getModelDetail(modelId: string): Promise<ModelDetailStats> {
  return request<ModelDetailStats>(`/models/${modelId}`);
}

export async function getModelHeadToHead(modelId: string): Promise<HeadToHeadRecord[]> {
  return request<HeadToHeadRecord[]>(`/models/${modelId}/head-to-head`);
}

export async function getStatsOverview(): Promise<PlatformOverview> {
  return request<PlatformOverview>("/stats/overview");
}

export async function getQueueStatus(): Promise<QueueStatus> {
  return request<QueueStatus>("/games/queue-status");
}

export async function getOpeningStats(): Promise<OpeningStats[]> {
  return request<OpeningStats[]>("/stats/openings");
}

export async function compareModels(modelA: string, modelB: string): Promise<HeadToHeadComparison> {
  const qs = new URLSearchParams({ model_a: modelA, model_b: modelB });
  return request<HeadToHeadComparison>(`/models/compare?${qs}`);
}

export async function getEloHistory(modelId: string): Promise<EloHistoryPoint[]> {
  return request<EloHistoryPoint[]>(`/models/${modelId}/elo-history`);
}

// Module-level cache for OpenRouter models with 5-minute TTL
let _openRouterCache: OpenRouterModel[] | null = null;
let _openRouterCacheTime = 0;
const OPENROUTER_CACHE_TTL_MS = 5 * 60 * 1000;

export async function fetchOpenRouterModels(): Promise<OpenRouterModel[]> {
  if (_openRouterCache !== null && Date.now() - _openRouterCacheTime < OPENROUTER_CACHE_TTL_MS) {
    return _openRouterCache;
  }
  const models = await request<OpenRouterModel[]>("/openrouter/models");
  _openRouterCache = models;
  _openRouterCacheTime = Date.now();
  return models;
}
